import {
    Button,
    Checkbox,
    Col,
    Collapse,
    DatePicker,
    Drawer,
    Form,
    Input,
    InputNumber,
    Modal,
    Rate,
    Row,
    Select,
    Spin
} from "antd";
import React, {useEffect, useMemo, useState} from "react";
import BookCover from "@components/book-cover";
import {InfoIcon} from "@components/icons/Icons";
import {
    useCollectionAddIssuesByFiltersMutation,
    useCollectionAddIssuesMutation,
    useCollectionUpdateIssueMutation,
    useGetCollectionLocationsQuery,
    useGetSignedByIdsQuery,
    useGetTransactionVenuesQuery
} from "@mwServices/User";
import moment from "moment";
import TextArea from "antd/es/input/TextArea";
import {DebounceSelect} from "@components/debounce-select";
import {Issue, useRemoveLocationFromCollectionMutation} from "@mwServices/Issue";
import {fetchSignedByList, showResponse} from "@services/Api";
import {useGetBoxesQuery} from "@mwServices/Boxes";
import {formatGrade, formatMoney, formatTitleCase} from "@lib/format";
import ContactsSelect from "@components/contacts-select";
import useHasPermission from "@hooks/useHasPermission";
import {WarningOutlined} from "@ant-design/icons";
import useSubscriptionType from "@hooks/useSubscriptionType";
import {showInfoModal} from "@lib/showModal";
import {useWindowSize} from "@components/window-size-provider";
import {useWatch} from "antd/es/form/Form";
import {stopClickEvent} from "@lib/utils";
import {BoxesAddDrawer} from "@components/boxes-add-drawer";
import {Grades} from "@lib/constants";

const { Panel } = Collapse;

interface AddEditComicDrawerProps {
    issue?: any;
    addIssuesByFilters?: any;
    addIssuesByFiltersCount?: number;
    addIssueIds?: number[];
    visible: boolean;
    onClose: () => void;
}

const defaultProps: AddEditComicDrawerProps = {
    issue: {},
    visible: false,
    onClose: () => {},
}

const AddEditComicDrawerContent = ({issue, addIssueIds, form, onClose, addIssuesByFilters, issueCount, setIsSubmitting}) => {
    const isEditComicView= !!(issue && issue.id);
    const [postUpdateIssues, updateIssuesResponse] = useCollectionUpdateIssueMutation();
    const [postAddIssues, addIssuesResponse] = useCollectionAddIssuesMutation();
    const [postAddIssuesByFilters, addIssuesByFiltersResponse] = useCollectionAddIssuesByFiltersMutation();

    const [addBox, setAddBox] = useState({show: false, issue_ids: [], series_ids: []});
    const [showMore, setShowMore] = useState(isEditComicView);

    const initForm = useMemo<Issue>(() => (isEditComicView ? {
        ...issue,
        grade: issue.grade ? formatGrade(issue.grade) : "0",
        purchase_date: issue.purchase_date ? moment(issue.purchase_date) : null,
        box_ids: issue.box_ids ? issue.box_ids.split(',') : [],
        signed_by_ids: issue.signed_by_ids ? issue.signed_by_ids.split(',') : [],
    } : {
        graded_by: "RAW",
        grade: "0",
        quantity: 1,
        box_ids: [],
        signed_by_ids: [],
    }), [issue, addIssueIds]);

    const locationsQuery = useGetCollectionLocationsQuery({});
    const [postRemoveLocation, removeLocationResponse] = useRemoveLocationFromCollectionMutation();
    const [locations, setLocations] = useState<{ location: string, count: number | string }[]>([]);
    const [locationSearch, setLocationSearch] = useState("");
    const maxLocationLength = 100;
    const boxesQuery = useGetBoxesQuery({filters: {}});
    const signedByQuery = useGetSignedByIdsQuery({ids: issue.signed_by_ids});
    const venuesQuery = useGetTransactionVenuesQuery();
    const [venues, setVenues] = useState<{ venue: string }[]>([]);

    const subscriptionType = useSubscriptionType();
    const allowBoxes = useMemo(() => (subscriptionType.isTrial || subscriptionType.isUnlimited), [subscriptionType]);

    useEffect(() => {
        showResponse(updateIssuesResponse, 'Collection item updated.', 'Failed to update the collection item.');
    }, [updateIssuesResponse]);

    useEffect(() => {
        showResponse(addIssuesResponse, 'Collection items added.', 'Failed to add the collection items.');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addIssuesResponse]);

    useEffect(() => {
        showResponse(addIssuesByFiltersResponse, 'Collection items added.', 'Failed to add the collection items.');
    }, [addIssuesByFiltersResponse]);

    useEffect(() => {
        if(removeLocationResponse.data && removeLocationResponse.data.success){
            if(form.getFieldValue('location') === removeLocationResponse.data.location){
                form.setFieldValue('location', '');
            }
            locationsQuery.refetch();
        }
        showResponse(removeLocationResponse, 'Location removed.', 'Failed to remove the location from the collection.');
    }, [removeLocationResponse]);
    

    const boxesOptions = useMemo<{ label, value }[]>(() => {
        return boxesQuery.data ? boxesQuery.data.boxes.map(box => ({
            label: box.name, value: box.id,
        })) : [];
    }, [boxesQuery.data]);
    const [filteredBoxesOptions, setFilteredBoxesOptions] = useState(boxesOptions);
    useEffect(() => setFilteredBoxesOptions(boxesOptions), [boxesOptions]);

    const signedByOptions = useMemo<{ label, value }[]>(() => {
        return signedByQuery.data ? signedByQuery.data.map(item => ({
            label: formatTitleCase(item.name), value: item.id,
        })) : [];
    }, [signedByQuery.data]);

    useEffect(() => {
        if (locationsQuery.data) {
            setLocations(locationsQuery.data);
        }
    }, [locationsQuery.data]);

    useEffect(() => {
        if (venuesQuery.data) {
            setVenues(venuesQuery.data);
        }
    }, [venuesQuery.data]);

    const submitUpdates = async (values) => {
        setIsSubmitting(true);

        let sanitisedData = {
            issue_ids: undefined,
            filters: undefined,
            issue_id: issue.id,
            collection_item_id: issue.collection_item_id,
            graded_by: issue.graded_by,
            grade: issue.grade,
            purchase_date: issue.purchase_date,
            purchase_price: issue.purchase_price,
            location: issue.location,
            notes: issue.notes,
            story_rating: issue.story_rating,
            art_rating: issue.art_rating,
            overall_rating: issue.overall_rating,
            recommend: issue.recommend,
            user_value: issue.user_value,
            box_ids: issue.box_ids,
            purchase_trx_id: issue.purchase_trx_id,
            purchase_shipping_co: issue.purchase_shipping_co,
            purchase_shipping_fees: issue.purchase_shipping_fees,
            purchase_tracking_number: issue.purchase_tracking_number,
            purchase_fees: issue.purchase_fees,
            purchase_venue: issue.purchase_venue,
            purchase_notes: issue.purchase_notes,
            purchase_additional_expenses: issue.purchase_additional_expenses,
            purchase_contact_id: issue.purchase_contact_id,
            purchase_contact_rating: issue.purchase_contact_rating,
            purchase_contact_notes: issue.purchase_contact_notes,
            label_type: issue.label_type,
            cert_number: issue.cert_number,
            review_internal_art: issue.review_internal_art,
            review_review: issue.review_review,
            signed_by_ids: issue.signed_by_ids,
            page_quality: issue.page_quality,
            custom_label: issue.custom_label,
            get_it_slabbed: issue.get_it_slabbed,
        };

        sanitisedData = {...sanitisedData, ...values};

        if (values.user_value === 0) sanitisedData.user_value = null
        if (values.purchase_date) sanitisedData.purchase_date = values.purchase_date.format("YYYY-MM-DD")
        if(values.box_ids)  sanitisedData.box_ids = values.box_ids.join(',')
        if(values.signed_by_ids) sanitisedData.signed_by_ids = values.signed_by_ids.join(',')

        if(isEditComicView){
            await postUpdateIssues(sanitisedData);
        } else {
            if(addIssuesByFilters){
                sanitisedData.filters = addIssuesByFilters;
                await postAddIssuesByFilters(sanitisedData);
            }else{
                sanitisedData.issue_ids = addIssueIds;
                await postAddIssues(sanitisedData);
            }
        }

        setIsSubmitting(false);
        form.resetFields();
        onClose();
    };

    useEffect(() => {
        setIsSubmitting(false);
        form.resetFields();
        setShowMore(isEditComicView)
    }, [issue, addIssueIds]);

    const gradedBy = useWatch('graded_by', form);
    const showRawFields = useMemo(() => gradedBy === 'RAW', [gradedBy]);

    const purchaseContactId = useWatch('purchase_contact_id', form);
    const showSellerFields = useMemo(() => !!purchaseContactId, [purchaseContactId]);

    const purchasePrice = useWatch('purchase_price', form);
    const shippingFees = useWatch('purchase_shipping_fees', form);
    const addFees = useWatch('purchase_additional_expenses', form);
    const totalComicCost = useMemo(() => {
        return [purchasePrice, shippingFees, addFees].reduce((newTotal, value: any) => (!isNaN(value)) ? newTotal + Number(value) : newTotal, 0);
    }, [purchasePrice, shippingFees, addFees]);


    return (
        <>
            {isEditComicView && (
                <>
                    <Row>
                        <Col span={24} className="flex-align-right">
                            <Button
                                className={'btn-price-guide'}
                                type="link"
                                href={`/issue/${issue.slug}`}
                            >Go To Price Guide &gt;</Button>
                        </Col>
                    </Row>
                    <Row>
                        <Col span={5}>
                            <BookCover issue={issue} size={'small'} />
                        </Col>
                        <Col span={19} className={'title-block'}>
                            <div>
                                <b>{issue.series_title}: {issue.issue_number}</b><br/>
                                {issue.is_variant ? issue.variant_name : 'Regular'}
                            </div>
                        </Col>
                    </Row>
                </>
            )}

            {!isEditComicView && (
                <Row>
                    <Col span={24}>
                        <div style={{fontSize: 18, fontWeight: 800, color: '#0E6993'}}>{issueCount} Comics Selected</div>
                        <div style={{fontSize: 14, color: '#0E6993'}}>Selections made here will be applied to all the comics you've chosen to add.</div>
                    </Col>
                </Row>
            )}

            <Form
                labelCol={{span: 10}}
                initialValues={initForm}
                wrapperCol={{span: 14}}
                form={form}
                name="control-hooks"
                onFinish={submitUpdates}
            >
            <Row>
                <Col span={24}>
                    <Collapse ghost className="editSections" defaultActiveKey={[1,2,3,4,5,7,8]} expandIconPosition={"end"}>
                        <Panel header={"Grading Details"} key={1}>
                            <Row>
                                <Col span={3} className={'align-left-top'}>
                                    <Button onClick={() =>{
                                            showInfoModal({
                                                title: "Grading Details",
                                                content: (
                                                    <>
                                                        <p>The options presented here allow you to add information regarding the graded (AKA: Slabbed) comics you have entered into your CovrPrice collection.</p>
                                                        <ul>
                                                            <li>Label Type - Allows you to identify the specific label (e.g. Blue Label, Restored Label, Signature Label, etc) provided to you by the grading company you entered in the Condition Details section above.</li>
                                                            <li>Certification # - Allows you to enter the certification number provided to you from the grading company you entered in the Grade Details section above.</li>
                                                            <li>Custom Label - Allows you to enter information for a custom label (e.g. Hobgoblin, Moon Kight, etc)</li>
                                                            <li>Slab Quality - Allows you to select options to describe the quality of the case your graded comic is in.</li>
                                                            <li>Page Quality - Allows you to enter page quality notes received from the grading company (e.g. White, Off-White, Light Tan, etc)</li>
                                                            <li>Grader Notes -  Allow you to add notes from the grading company or your own.</li>
                                                            <li>Get it Re-Slabbed - Allows you to identify your graded comics that you wish to send in to a grading company to have graded and slabbed again.</li>
                                                        </ul>
                                                    </>
                                                ),
                                            });
                                        }} type="link" icon={<InfoIcon className="iconLarge"/>}/>
                                </Col>
                                <Col span={21}>
                                    {!isEditComicView && (
                                        <Form.Item label="Quantity" name="quantity">
                                            <InputNumber min={0} precision={0}/>
                                        </Form.Item>
                                    )}

                                    <Form.Item label="Raw / Slabbed" name="graded_by">
                                        <Select>
                                            <Select.Option value="RAW">RAW</Select.Option>
                                            <Select.Option value="CGC">CGC</Select.Option>
                                            <Select.Option value="CBCS">CBCS</Select.Option>
                                            <Select.Option value="PSA">PSA</Select.Option>
                                            <Select.Option value="PGX">PGX</Select.Option>
                                            <Select.Option value="EGS">EGS</Select.Option>
                                            <Select.Option value="BECKETT">BECKETT</Select.Option>
                                            <Select.Option value="HALO">HALO</Select.Option>
                                            <Select.Option value="OTHER">OTHER</Select.Option>
                                        </Select>
                                    </Form.Item>

                                    <Form.Item label="Grade" name="grade">
                                        <Select>
                                            <Select.Option value="0">No Rating</Select.Option>
                                            {Grades.slabbed.map(grade => (
                                                <Select.Option key={grade.short_desc} value={formatGrade(grade.high)}>{formatGrade(grade.high)} - {grade.long_desc}</Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>

                                    <Form.Item label="General Notes" name={`notes`}>
                                        <TextArea rows={4} placeholder="(optional)" maxLength={1000} showCount/>
                                    </Form.Item>

                                    {showRawFields && (
                                        <Form.Item label="Get It Slabbed" name={`get_it_slabbed`} valuePropName="checked">
                                            <Checkbox />
                                        </Form.Item>
                                    )}
                                </Col>
                            </Row>

                            {!showRawFields && (
                                <Row>
                                    <Col span={3} className="align-left-top mar-top-20 pad-top-10">
                                        <Button onClick={() =>{
                                            showInfoModal({
                                                title: "Graded Comic Info",
                                                content: (
                                                    <>
                                                        <p>
                                                            The options presented here allow you to add information regarding the graded
                                                            (AKA: Slabbed) comics you have entered into your CovrPrice collection.
                                                        </p>
                                                        <ul>
                                                            <li>
                                                                Label Type - Allows you to identify the specific label (e.g. Blue Label,
                                                                Restored Label, Signature Label, etc) provided to you by the grading company
                                                                you entered in the Condition Details section above.
                                                            </li>
                                                            <li>
                                                                Certification # - Allows you to enter the certification number provided to
                                                                you from the grading company you entered in the Condition Details section above.
                                                            </li>
                                                            <li>
                                                                Custom Label -  Allows you to enter information for a custom label
                                                                (e.g. Hobgoblin, Moon Kight, etc)
                                                            </li>
                                                            <li>
                                                                Slab Quality - Allows you to select options to describe the quality of the
                                                                case your graded comic is in.
                                                            </li>
                                                            <li>
                                                                Page Quality - Allows you to enter page quality notes received from the grading
                                                                company (e.g. White, Off-White, Light Tan, etc)
                                                            </li>
                                                            <li>
                                                                Grader Notes -  Allow you to add notes from the grading company or your own.
                                                            </li>
                                                            <li>
                                                                Get it Re-Slabbed - Allows you to identify your graded comics that you wish to send
                                                                in to a grading company to have graded and slabbed again.
                                                            </li>
                                                        </ul>
                                                    </>
                                                ),
                                            });
                                        }} type="link" icon={<InfoIcon className="iconLarge"/>}/>
                                    </Col>
                                    <Col span={21} className="mar-top-20 pad-top-10 border-top">
                                        <Form.Item label="Label" name="label_type">
                                            <Select placeholder="Select Label">
                                                <Select.Option value={0}>None</Select.Option>
                                                <Select.Option value={1}>CGC Universal Grade</Select.Option>
                                                <Select.Option value={2}>CGC Signature Series</Select.Option>
                                                <Select.Option value={3}>CGC Qualified Grade</Select.Option>
                                                <Select.Option value={4}>CGC Restored Grade</Select.Option>
                                                <Select.Option value={5}>CGC Pedigree Grade</Select.Option>
                                                <Select.Option value={6}>CGC Conserved Grade</Select.Option>
                                                <Select.Option value={7}>CGC Signature Series Qualified</Select.Option>
                                                <Select.Option value={8}>CGC Signature Series Restored</Select.Option>
                                                <Select.Option value={9}>CBCS Certified</Select.Option>
                                                <Select.Option value={10}>CBCS Authentic Signature</Select.Option>
                                                <Select.Option value={11}>CBCS Verified Signature</Select.Option>
                                                <Select.Option value={12}>CBCS Verified Red Label (legacy)</Select.Option>
                                                <Select.Option value={13}>Other</Select.Option>
                                            </Select>
                                        </Form.Item>

                                        {(isEditComicView || (issueCount === 1)) && (
                                            <>
                                                <Form.Item label="Certification #" name="cert_number">
                                                    <Input placeholder="" />
                                                </Form.Item>
                                            </>
                                        )}

                                        <Form.Item label="Custom Label" name="custom_label">
                                            <Input placeholder="Type Label Name" />
                                        </Form.Item>
                                        <Form.Item label="Slab Quality" name="slab_quality">
                                            <Select placeholder="Select Quality">
                                                <Select.Option value={0}>Unknown</Select.Option>
                                                <Select.Option value={1}>Perfect</Select.Option>
                                                <Select.Option value={2}>Good</Select.Option>
                                                <Select.Option value={3}>Scratched / Blemish</Select.Option>
                                                <Select.Option value={4}>Broken</Select.Option>
                                            </Select>
                                        </Form.Item>
                                        <Form.Item label="Page Quality" name="page_quality">
                                            <Select placeholder="Select Quality">
                                                <Select.Option value={0}>Unknown</Select.Option>
                                                <Select.Option value={1}>White</Select.Option>
                                                <Select.Option value={2}>Off-White to White</Select.Option>
                                                <Select.Option value={3}>Off-White</Select.Option>
                                                <Select.Option value={4}>Cream to Off-White</Select.Option>
                                                <Select.Option value={5}>Cream</Select.Option>
                                                <Select.Option value={6}>Light Tan to Off-White</Select.Option>
                                                <Select.Option value={7}>Light Tan to Cream</Select.Option>
                                                <Select.Option value={8}>Light Tan</Select.Option>
                                                <Select.Option value={9}>Tan to Off-White</Select.Option>
                                                <Select.Option value={10}>Tan to Cream</Select.Option>
                                                <Select.Option value={11}>Tan</Select.Option>
                                                <Select.Option value={12}>Dark Tan to Off-White</Select.Option>
                                                <Select.Option value={13}>Dark Tan</Select.Option>
                                                <Select.Option value={14}>Brown to Off-White</Select.Option>
                                                <Select.Option value={15}>Brown to Tan</Select.Option>
                                                <Select.Option value={16}>Brown</Select.Option>
                                                <Select.Option value={17}>Brown / Brittle</Select.Option>
                                                <Select.Option value={18}>Slightly Brittle</Select.Option>
                                                <Select.Option value={19}>Brittle</Select.Option>
                                                <Select.Option value={20}>Pink</Select.Option>
                                                <Select.Option value={21}>Cream to Pink</Select.Option>
                                                <Select.Option value={22}>Tan to Pink</Select.Option>
                                                <Select.Option value={23}>Blue</Select.Option>
                                            </Select>
                                        </Form.Item>

                                        {(isEditComicView || (issueCount === 1)) && (
                                            <>
                                                <Form.Item label="Grader Notes" name={`grader_notes`}>
                                                    <TextArea rows={4} placeholder="optional" maxLength={1000} showCount/>
                                                </Form.Item>
                                            </>
                                        )}

                                        <Form.Item label="Get It Re-Slabbed" name={`get_it_slabbed`} valuePropName="checked">
                                            <Checkbox />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            )}

                            {!showMore && (
                                <Row>
                                    <Col span={24} className={"flex-align-center"}>
                                        <Button type={"link"} style={{}} onClick={() => setShowMore(true)}>Add More Details</Button>
                                    </Col>
                                </Row>
                            )}
                        </Panel>

                        {showMore && (
                            <>
                                <Panel header="Storage Location / Virtual Boxes" key={2}>
                                    <Row>
                                        <Col span={3}  className={'align-left-top'}>
                                            <Button  onClick={() =>{
                                                showInfoModal({
                                                    title: "Storage Location / Virtual Boxes",
                                                    content: (
                                                        <>
                                                            <p>
                                                                The options presented here allow you to tag a comic with a specific STORAGE LOCATION and/or
                                                                to assign it to a VIRTUAL BOX (AKA: a sub-collection) within your CovrPrice collection.
                                                            </p>
                                                            <ul>
                                                                <li>
                                                                    Storage Location / Box - Use this option to identify a physical place where you keep
                                                                    this comic. For Example, you might create a location for “Bedroom - Longbox A”. You will
                                                                    be able to filter your entire collection by the LOCATIONS you create.
                                                                </li>
                                                                <li>
                                                                    Virtual Box(es) - Use this option to group your comics into “sub-collections” or categories.
                                                                    Once a virtual box (VB) is created, it will live within the MY VIRTUAL BOXES section
                                                                    (found via the tab option within the Collection section) Comics placed in a VB will be grouped
                                                                    together so that you can quickly see their total value and manage them. For example, you might
                                                                    create a VB called “Things to Sell” or “Alex Ross Covers”. IMPORTANT NOTE: To add a comic to a
                                                                    VB, you first have to have that comic in your collection. Changes that you make to a comic in
                                                                    either the main collection section or in the My Virtual Boxes section, will be made everywhere
                                                                    that comic exists. For example, deleting a comic in the main collection will delete it everywhere.
                                                                </li>
                                                            </ul>
                                                        </>
                                                    ),
                                                });
                                            }} type="link" icon={<InfoIcon className="iconLarge"/>}/>
                                        </Col>
                                        <Col span={21}>
                                            <Form.Item label="Storage Location / Box" name="location">
                                                <Select
                                                    showSearch
                                                    onSearch={(value) => {
                                                        let locationData = (value != '') ? value.substring(0, maxLocationLength) : locationSearch
                                                        if (locationsQuery.data) {
                                                            setLocations([{location: locationData, count: 'new'}, ...locationsQuery.data]);
                                                        } else {
                                                            setLocations([{location: locationData, count: 'new'}]);
                                                        }

                                                        setLocationSearch(value.substring(0, maxLocationLength));
                                                    }}
                                                    searchValue={locationSearch}
                                                    placeholder="Type Location Name"
                                                >
                                                    <Select.Option value="">None</Select.Option>
                                                    {locations.filter(function(location) {
                                                        return location.location != '[No Location]'
                                                    }).map((location, i) => (
                                                        <Select.Option
                                                            key={i}
                                                            value={location.location}>
                                                            {location.location} ({location.count})
                                                            {+location.count > 0 && (
                                                                <a
                                                                    className="location-delete"
                                                                    onClick={(event) => {
                                                                        stopClickEvent(event);
                                                                        Modal.confirm({
                                                                            autoFocusButton: null,
                                                                            icon: <WarningOutlined />,
                                                                            content: (<div>
                                                                                <div><b>Remove Location</b></div>
                                                                                <div className={'pad-top-10'}>
                                                                                    <p>Are you sure?</p>
                                                                                    <p>Selecting this option will permanently remove location "{location.location}" from all your comics that have been designated with this location. If you do remove this location tag, comics in this location will still remain in your collection, just not be designated as "{location.location}".</p>
                                                                                    <p>To remove this location from this single comic, click the Location dropdown option and select "none".</p>
                                                                                </div>
                                                                            </div>),
                                                                            onOk() {
                                                                                postRemoveLocation({location: location.location});
                                                                            },
                                                                        })
                                                                    }}
                                                                >
                                                                    X
                                                                </a>
                                                            )}
                                                        </Select.Option>
                                                    ))}
                                                </Select>
                                            </Form.Item>
                                            <Form.Item label="Virtual Box(es)" name="box_ids">
                                                <Select
                                                    defaultValue={issue.box_ids ? issue.box_ids.split(',') : []}
                                                    onChange={(value) =>{
                                                        form.setFieldValue('box_ids', value)
                                                    }}
                                                    mode="multiple"
                                                    allowClear
                                                    className={'width-100'}
                                                    placeholder="Type Name of VB"
                                                    disabled={!allowBoxes}
                                                    options={filteredBoxesOptions}
                                                    filterOption={false}
                                                    showSearch
                                                    onSearch={(value) => {
                                                        const newOptions = boxesOptions.filter((option) => option.label.toLowerCase().indexOf(value.toLowerCase().trim()) > -1);
                                                        setFilteredBoxesOptions(newOptions);
                                                    }}
                                                />
                                            </Form.Item>
                                            <div className={'text-right mar-top-neg10'}>
                                                <Button className={'pad-0'} disabled={!allowBoxes} type="link" onClick={() => setAddBox({show:true, issue_ids: [], series_ids: []})} >+Add a VB</Button>
                                            </div>
                                        </Col>
                                    </Row>
                                </Panel>
                                <Panel header="Autograph / Signature Info" key={3}>
                                    <Row>
                                        <Col span={3}  className={'align-left-top'}>
                                            <Button  onClick={() =>{
                                                showInfoModal({
                                                    title: "Autograph / Signature Info",
                                                    content: (
                                                        <>
                                                            <p>The option presented here allows you to add information regarding signatures/autographs that this comic may have.</p>
                                                            <p>Signed By - Allows you to add the name of one or more signatories for this comic. You can select from previously added signatories or add new ones.</p>
                                                        </>
                                                    ),
                                                });
                                            }} type="link" icon={<InfoIcon className="iconLarge"/>}/>
                                        </Col>
                                        <Col span={21}>
                                            <Form.Item label="Signed By" name="signed_by_ids">
                                                <DebounceSelect
                                                    mode="multiple"
                                                    allowSearchAsOption
                                                    placeholder="Type Name(s)"
                                                    initOptions={signedByOptions}
                                                    fetchOptions={fetchSignedByList}
                                                    className={'width-100'}
                                                    onChange={(value: any)=>{
                                                        form.setFieldValue('signed_by_ids', value.map(item => item.value))
                                                    }}
                                                />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </Panel>
                                <Panel header="Enter My Own Value" key={4}>
                                    <Row>
                                        <Col span={3}  className={'align-left-top'}>
                                            <Button  onClick={() =>{
                                                showInfoModal({
                                                    title: "Enter My Own Value",
                                                    content: (
                                                        <>
                                                            <p>As a default, CovrPrice will assign our estimated FMV (i.e. Fair Market Value) to the comics you add to your collection taking into account any grade (e.g. 9.0, 9.6, etc) you may specify. For example, if you specify that a comic is a CGC 9.8, CovrPrice will look for the FMV that supports that grade. When there is no sales data available for that grade, CovrPrice will assign your comic with the “Most Common Condition (MCC)” value associated with that comic. For example, if no 9.8s sales exist, CovrPice might assign the value of a 9.0 to your comic, if that is the MCC).</p>
                                                            <p>This approach may lead to instances where you disagree with the values CovrPrice presents. The option shown here allows you to assign your own value (AKA: the “MyValue”) for any comic in your collection. The MyValue will live with your comic until you opt into the CovrPrice FMV via the option here.</p>
                                                        </>
                                                    ),
                                                });
                                            }} type="link" icon={<InfoIcon className="iconLarge"/>}/>
                                        </Col>
                                        <Col span={21}>
                                            <Form.Item label="My Value" name="user_value">
                                                <InputNumber min={0} addonBefore="$" precision={2}/>
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </Panel>
                                <Panel header="Purchase Info" key={5}>
                                    <Row>
                                        <Col span={3}  className={'align-left-top'}>
                                            <Button onClick={() =>{
                                                showInfoModal({
                                                    title: "Purchase Info",
                                                    content: (
                                                        <>
                                                            <p>
                                                                The options presented here allow you to add information regarding your purchase of this comic.
                                                                <ul>
                                                                    <li><b>Purchase Price</b> - Allows you to enter what you paid for this comic.</li>
                                                                    <li><b>Purchase Date</b> - Allows you to enter when you made the purchase of this comic.</li>
                                                                    <li><b>Sales Venue</b> - Allows you to identify the storefront that you used to purchase this comic. (e.g. eBay, Heritage Auctions, etc)</li>
                                                                    <li><b>Transaction #</b> - Allows you to enter a purchase related transaction number for your records.</li>
                                                                    <li><b>Tracking #</b> - Allows you to enter tracking / shipping numbers for your records.</li>
                                                                    <li><b>Shipping Fee</b> - Allows you to enter the cost of shipping.</li>
                                                                    <li><b>Additional Fees</b> - Allows you to enter any additional costs you may have paid for this comic. </li>
                                                                    <li><b>Purchase Notes</b>: Allows you to enter notes regarding this purchase for your records.</li>
                                                                </ul>
                                                            </p>
                                                        </>
                                                    ),
                                                });
                                            }} type="link" icon={<InfoIcon className="iconLarge"/>}/>
                                        </Col>
                                        <Col span={21}>

                                            <Form.Item label="Purchase Date" name="purchase_date">
                                                <DatePicker
                                                    className={'width-100'}
                                                    format={(value) => `${value.format("YYYY/MM/DD")}`}
                                                />
                                            </Form.Item>

                                            <Form.Item label="--" className="hide-form-label">
                                                <div>
                                                    Total Comic Cost:
                                                    <span className="font-bold mar-left-10">{formatMoney(totalComicCost, false, true)}</span>
                                                </div>
                                            </Form.Item>
                                            <Form.Item label="Purchase Price" name="purchase_price">
                                                <InputNumber min={0} addonBefore="$" precision={2} placeholder="Enter Purchase Price"/>
                                            </Form.Item>

                                            <Form.Item label="Shipping Fee" name={`purchase_shipping_fees`}>
                                                <InputNumber min={0} addonBefore="$" precision={2} placeholder=""/>
                                            </Form.Item>

                                            <Form.Item label="Additional Fees" name={`purchase_additional_expenses`}>
                                                <InputNumber min={0} addonBefore="$" precision={2} placeholder=""/>
                                            </Form.Item>

                                            <Form.Item label="Sales Venue" name={`purchase_venue`}>
                                                <Select
                                                    showSearch
                                                    onSearch={(value) => {
                                                        if (venuesQuery.data) {
                                                            setVenues([{venue: value}, ...venuesQuery.data]);
                                                        } else {
                                                            setVenues([{venue: value}]);
                                                        }
                                                        if(value && value !== '') form.setFieldValue('purchase_venue', value);
                                                    }}
                                                >
                                                    <Select.Option value="">None</Select.Option>
                                                    {venues.map((venue, i) => (
                                                        <Select.Option key={i}
                                                                       value={venue.venue.toLowerCase()}>{formatTitleCase(venue.venue)}</Select.Option>
                                                    ))}
                                                </Select>
                                            </Form.Item>

                                            <ContactsSelect label="Seller Info" name={`purchase_contact_id`} form={form} addButtonText="+Add Seller Details" />

                                            {showSellerFields && (
                                                <>
                                                    <Form.Item label="Seller Rating" name={`purchase_contact_rating`}>
                                                        <Rate />
                                                    </Form.Item>
                                                    <Form.Item label="Seller Notes" name={`purchase_contact_notes`}>
                                                        <TextArea rows={4} placeholder="(optional)" maxLength={1000} showCount/>
                                                    </Form.Item>
                                                </>
                                            )}

                                            <Form.Item label="Transaction #" name={"purchase_trx_id"}>
                                                <Input placeholder=""/>
                                            </Form.Item>

                                            <Form.Item label="Tracking #" name={`purchase_tracking_number`}>
                                                <Input placeholder=""/>
                                            </Form.Item>

                                            <Form.Item label="Purchase Notes" name="purchase_notes">
                                                <TextArea rows={4} placeholder="" maxLength={1000} showCount/>
                                            </Form.Item>

                                        </Col>
                                    </Row>
                                </Panel>
                                <Panel header="My Comic Rating" key={8}>
                                    <Row>
                                        <Col span={3}  className={'align-left-top'}>
                                            <Button onClick={() =>{
                                                showInfoModal({
                                                    title: "My Comic Rating",
                                                    content: (
                                                        <p>
                                                            The options presented here allow you to rate your comic along the options presented. NOTE: this information you provide here may be used in the future by CovrPrice to provide you with comic recommendations.

                                                            <ul>
                                                                <li><b>Recommend?</b> - Allows you to state if you would or would not recommend this comic to others.</li>
                                                                <li><b>Overall</b> - A 5 star rating (1 = Lowest / 5 = Highest) on your overall experience with this comic.</li>
                                                                <li><b>Cover Art</b> - A 5 star rating (1 = Lowest / 5 = Highest) on your overall experience regarding the cover art of the comic.</li>
                                                                <li><b>Internal Art</b> - A 5 star rating (1 = Lowest / 5 = Highest) on your overall experience regarding the internal art of the comic.</li>
                                                                <li><b>Story</b> - A 5 star rating (1 = Lowest / 5 = Highest) on your overall experience regarding the story of this comic.</li>
                                                                <li><b>Comic Review</b> - Allows you to provide a notes or a review about this comic</li>
                                                            </ul>
                                                        </p>
                                                    ),
                                                });
                                            }} type="link" icon={<InfoIcon className="iconLarge"/>}/>
                                        </Col>
                                        <Col span={21}>
                                            <Form.Item label="Overall" name="overall_rating">
                                                <Rate/>
                                            </Form.Item>
                                            <Form.Item label="Cover Art" name="art_rating">
                                                <Rate/>
                                            </Form.Item>
                                            <Form.Item label="Internal Art" name="review_internal_art">
                                                <Rate />
                                            </Form.Item>
                                            <Form.Item label="Story" name="story_rating">
                                                <Rate/>
                                            </Form.Item>

                                            <Form.Item label="Recommend" name="recommend">
                                                <Select>
                                                    <Select.Option value={0}>No Recommendation</Select.Option>
                                                    <Select.Option value={1}>1 - Hate It!</Select.Option>
                                                    <Select.Option value={2}>2</Select.Option>
                                                    <Select.Option value={3}>3</Select.Option>
                                                    <Select.Option value={4}>4</Select.Option>
                                                    <Select.Option value={5}>5 - Neutral.</Select.Option>
                                                    <Select.Option value={6}>6</Select.Option>
                                                    <Select.Option value={7}>7</Select.Option>
                                                    <Select.Option value={8}>8</Select.Option>
                                                    <Select.Option value={9}>9</Select.Option>
                                                    <Select.Option value={10}>10 - Love It!</Select.Option>
                                                </Select>
                                            </Form.Item>

                                            <Form.Item label="Comic Review" name="review_review">
                                                <TextArea rows={4} placeholder="" maxLength={1000} showCount />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </Panel>
                            </>
                        )}
                    </Collapse>
                </Col>
            </Row>
        </Form>

        <BoxesAddDrawer
            visible={addBox.show}
            issue_ids={addBox.issue_ids}
            series_ids={addBox.series_ids}
            onClose={() => {setAddBox({...addBox, show: false})}}
        />
    </>
    );
};

const AddEditComicDrawer = ({issue, visible, onClose, addIssueIds, addIssuesByFilters, addIssuesByFiltersCount}: AddEditComicDrawerProps) => {
    const isEditComicView= !!(issue && issue.id);
    const [form] = Form.useForm();
    const windowDimensions = useWindowSize();
    const [drawerWidth, setDrawerWidth] = useState(450);
    const isAdmin = useHasPermission('admin');

    useEffect(() => {
        const newWidth = (windowDimensions.width < 450) ? windowDimensions.width : 450;
        if(newWidth !== drawerWidth) setDrawerWidth(newWidth);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [windowDimensions]);

    const onCloseExec = (event) => {
        if(event && event.target && event.target.className &&
            event.target.className.indexOf && event.target.className.indexOf('ant-drawer-mask') !== -1){
            //click outside
            if(form.isFieldsTouched()){
                //Form edited, save.
                form.submit();
            }else{
                //No changes, close no save.
                setIsSubmitting(false);
                form.resetFields();
                onClose();
            }
        } else {
            //click close button or other close event.
            setIsSubmitting(false);
            form.resetFields();
            onClose();
        }
    };

    const issueCount = addIssueIds ? addIssuesByFiltersCount? addIssuesByFiltersCount : addIssueIds.length : 0;
    const quantity = Form.useWatch('quantity', form);
    const addIssueCount = quantity ? issueCount*quantity : issueCount;

    const [isSubmitting, setIsSubmitting] = useState(false);

    return (
            <Drawer
                title={isEditComicView ? "Edit Issue" : "Add to Collection"}
                className={"add-edit-comic-drawer"}
                headerStyle={{paddingTop: isAdmin ? 50 : 16}}
                width={drawerWidth}
                placement="right"
                onClose={onCloseExec}
                open={visible}
                footer={
                    <Row justify="center">
                        <Col >
                            <Button className="outlineButton thickButton width-250px" disabled={isSubmitting} onClick={() => form.submit()}>
                                {isEditComicView ? "SAVE" : `ADD ${addIssueCount} COMICS`}
                            </Button>
                        </Col>
                    </Row>
                }
            >
                <Spin spinning={isSubmitting} size="large">
                    {(issue || addIssueIds) && (
                        <AddEditComicDrawerContent
                            issue={issue ? issue : {}}
                            onClose={onCloseExec}
                            form={form}
                            addIssueIds={addIssueIds}
                            addIssuesByFilters={addIssuesByFilters}
                            issueCount={issueCount}
                            setIsSubmitting={setIsSubmitting}
                        />
                    )}
                </Spin>
            </Drawer>
    );
};

AddEditComicDrawer.defaultProps = defaultProps;

export default AddEditComicDrawer;